import React, { useEffect, useState } from "react";
import styles from "./Register.module.scss";
import EmailValidationV2 from "../../components/UI/EmailValidation/EmailValidation.tsx";
import CodeValidationV2 from "../../components/UI/CodeValidation/CodeValidation.tsx";
import PasswordSetupV2 from "../../components/UI/PasswordSetup/PasswordSetup.tsx";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { loginWithGoogle } from "../../store/actions/authActions.js";
import { useRegistrationFlow } from "../../hooks/registration/useRegistrationFlow.ts";
import { getFromLoginValidation, getRegistrationEmail, clearFromLoginValidation } from "../../utils/registration/registrationStorage.ts";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";

const RegisterNewV2: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const registrationFlow = useRegistrationFlow();
  
  const [isFromLoginValidation, setIsFromLoginValidation] = useState<boolean>(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const stepParam = urlParams.get('step');
    const fromLoginValidation = getFromLoginValidation();
    const emailFromLogin = getRegistrationEmail();

    if (stepParam === 'code-validation' && fromLoginValidation && emailFromLogin) {
      registrationFlow.setEmail(emailFromLogin);
      registrationFlow.setStep('code');
      setIsFromLoginValidation(true);
      clearFromLoginValidation();
    }
  }, [location.search, registrationFlow]);

  const handleGoogleSuccess = async (idToken: string): Promise<void> => {
    try {
      (dispatch as any)(loginWithGoogle(idToken, navigate, { showProfilePostModal: true }));
    } catch (error) {
      console.error("Error al iniciar sesión con Google:", error);
    }
  };

  const renderCurrentStep = () => {
    switch (registrationFlow.currentStep) {
      case "email":
        return (
          <EmailValidationV2
            onGoogleSuccess={handleGoogleSuccess}
          />
        );
      case "code":
        return (
          <CodeValidationV2
            email={registrationFlow.email || ""}
            specialMessage={isFromLoginValidation ? "Nuevo código enviado" : undefined}
          />
        );
      case "password":
        return (
          <PasswordSetupV2
            email={registrationFlow.validatedEmail || registrationFlow.email || ""}
          />
        );
      default:
        return null;
    }
  };

  const renderCurrentStepTitle = () => {
    switch (registrationFlow.currentStep) {
        case "email":
          return "Sumate a la comunidad";
        case "code":
          return "Verifica tu email";
        case "password":
          return "Creá tu cuenta";
        default:
          return null;
      }
  };

  const renderCurrentStepSubtitle = () => {
    switch (registrationFlow.currentStep) {
      case "email":
        return "Una plataforma pensada para profesionales y empresas de la construcción.";
      case "code":
        return "Te enviamos un código de verificación.";
      case "password":
        return "Este va a ser tu acceso a Edify.";
      default:
        return null;
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1 
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {renderCurrentStepTitle()}
          </motion.h1>
          <motion.p 
            className={styles.mainSubtitle}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {renderCurrentStepSubtitle()}
          </motion.p>
        </div>

        <div className={styles.stepContainer}>
          {renderCurrentStep()}
        </div>

        <motion.div 
          className={styles.footer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className={styles.footerText}>
            ¿Ya tenés una cuenta?{" "}
            <a href="/" className={styles.footerLink}>
              Inicia sesión
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterNewV2;
